import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'antd';
import DrawerModal from '../../shared/DrawerModal';
import styles from './OpeningHoursModal.styles';
import {
  closeModal,
  getIsVisibleModal,
} from '../../../redux/store/ui/common/index';
import {
  selectSelectedArea,
  updateArea,
} from '../../../redux/store/appData/areas';
import { useIntl } from 'react-intl';
import DayController from './DayController';

const areaFallBackOpeningHours = [
  {
    dayOfWeekCode: 'MO',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'TU',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'WE',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'TH',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'FR',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'SA',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
  {
    dayOfWeekCode: 'SU',
    closedWholeDay: false,
    openAtHour: 0,
    closeAtHour: 0,
    openAtMinute: 0,
    closeAtMinute: 0,
  },
];

const OpeningHoursModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const visibleModal = useSelector(getIsVisibleModal('OpeningHoursModal'));
  const selectedArea = useSelector(selectSelectedArea);

  const openingHours =
    selectedArea?.openingHoursPerDay.length > 0
      ? selectedArea?.openingHoursPerDay
      : areaFallBackOpeningHours;

  const [daysOpeningHours, setDaysOpeningHours] = useState(
    openingHours?.reduce((acc, day) => {
      acc[day.dayOfWeekCode] = {
        closedWholeDay: day.closedWholeDay,
        openAtHour: day.openAtHour,
        closeAtHour: day.closeAtHour,
        openAtMinute: day.openAtMinute,
        closeAtMinute: day.closeAtMinute,
      };
      return acc;
    }, {}) || {}
  );

  const handleOnSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const openingHoursArray = Object.entries(daysOpeningHours).map(
      ([dayOfWeekCode, hours]) => ({
        dayOfWeekCode,
        closedWholeDay: hours.closedWholeDay,
        openAtHour: hours.openAtHour,
        closeAtHour: hours.closeAtHour,
        openAtMinute: hours.openAtMinute,
        closeAtMinute: hours.closeAtMinute,
      })
    );
    dispatch(
      updateArea(selectedArea?.id, { openingHoursPerDay: openingHoursArray })
    );
    dispatch(closeModal('OpeningHoursModal'));
  };

  const handleOnCancel = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(closeModal('OpeningHoursModal'));
  };

  const isUnchanged = useMemo(() => {
    return openingHours.every((day) => {
      const currentDay = daysOpeningHours[day.dayOfWeekCode];

      return (
        currentDay.closedWholeDay === day.closedWholeDay &&
        currentDay.openAtHour === day.openAtHour &&
        currentDay.closeAtHour === day.closeAtHour &&
        currentDay.openAtMinute === day.openAtMinute &&
        currentDay.closeAtMinute === day.closeAtMinute
      );
    });
  }, [daysOpeningHours, openingHours]);

  useEffect(() => {
    if (openingHours) {
      const newHours = openingHours?.reduce((acc, day) => {
        acc[day.dayOfWeekCode] = {
          closedWholeDay: day.closedWholeDay,
          openAtHour: day.openAtHour,
          closeAtHour: day.closeAtHour,
          openAtMinute: day.openAtMinute,
          closeAtMinute: day.closeAtMinute,
        };
        return acc;
      }, {});
      setDaysOpeningHours(newHours);
    }
  }, [openingHours]);

  return (
    <>
      <DrawerModal
        visible={visibleModal}
        onClose={() => {
          dispatch(closeModal('ArchiveAreaModal'));
        }}
      >
        <div className="opening-hours-modal">
          <div className="opening-hours-modal-wrapper">
            <div className="opening-hours-modal-inputs">
              <p className="opening-hours-title">
                {intl.formatMessage({
                  id: `fleetadmin.areas.edit.openingHours`,
                })}
              </p>
            </div>
            {Object.entries(daysOpeningHours).map(([day, hours]) => (
              <DayController
                key={day}
                day={day}
                hours={hours}
                onChange={setDaysOpeningHours}
              />
            ))}
          </div>
          <Button
            className="submit-button"
            onClick={handleOnSubmit}
            disabled={isUnchanged}
          >
            {intl.formatMessage({
              id: `submit`,
            })}
          </Button>
          <Button className="cancel-button" onClick={handleOnCancel}>
            {intl.formatMessage({
              id: `cancel`,
            })}
          </Button>
        </div>
      </DrawerModal>
      <style jsx>{styles}</style>
    </>
  );
};

export default OpeningHoursModal;
